import { createContext, useContext, useState, useEffect } from 'react';
import api from '../api/axios';
import { toast } from 'sonner';

const FavoritesContext = createContext();

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within FavoritesProvider');
  }
  return context;
};

export const FavoritesProvider = ({ children }) => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load favorites from localStorage on mount
  useEffect(() => {
    const savedFavorites = localStorage.getItem('favorites');
    if (savedFavorites) {
      try {
        setFavorites(JSON.parse(savedFavorites));
      } catch (error) {
        console.error('Error loading favorites:', error);
      }
    }
    setLoading(false);
  }, []);

  // Save favorites to localStorage whenever they change
  useEffect(() => {
    if (!loading) {
      localStorage.setItem('favorites', JSON.stringify(favorites));
    }
  }, [favorites, loading]);

  const isFavorite = (offerId) => {
    return favorites.includes(offerId);
  };

  const toggleFavorite = (offerId) => {
    if (isFavorite(offerId)) {
      setFavorites(prev => prev.filter(id => id !== offerId));
      toast.success('Retiré des favoris', {
        description: 'Cette offre a été retirée de vos favoris'
      });
    } else {
      setFavorites(prev => [...prev, offerId]);
      toast.success('Ajouté aux favoris ⭐', {
        description: 'Cette offre a été ajoutée à vos favoris'
      });
    }
  };

  const getFavoriteOffers = async () => {
    if (favorites.length === 0) return [];
    
    try {
      const response = await api.get('/offers', {
        params: { status: 'approved' }
      });
      return response.data.filter(offer => favorites.includes(offer.id));
    } catch (error) {
      console.error('Error loading favorite offers:', error);
      return [];
    }
  };

  const clearFavorites = () => {
    setFavorites([]);
    toast.success('Favoris effacés', {
      description: 'Tous vos favoris ont été retirés'
    });
  };

  const value = {
    favorites,
    loading,
    isFavorite,
    toggleFavorite,
    getFavoriteOffers,
    clearFavorites,
    favoritesCount: favorites.length
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};
